import { SetOptional } from 'type-fest'

import api from '@libs/api'

import DateUtils from '@utils/dates'
import { handleServiceError } from '@utils/errors'
import {
  ContactUserType,
  IApiData,
  ILead,
  IQueryFilters,
  LeadSource,
} from '@utils/types'
import { buildQueryString } from '@utils/url'

type ICreateLead = {
  firstName: string
  lastName: string
  companyEmail: string
  companyName?: string
  phone?: string
  userType?: ContactUserType
  message?: string
}

const getApiPathFromLeadSource = (source: LeadSource) => {
  switch (source) {
    case LeadSource.IT_CUSTOMER_LEAD_MAGNET:
      return '/leads/magnet-form'
    case LeadSource.CONTACT_FORM:
      return '/leads/contact-form'
    case LeadSource.WHITEPAPER_LEAD_MAGNET:
      return '/leads/whitepaper-magnet-form'
    default:
      throw new Error(`Api path not implemented for ${source}`)
  }
}

const findAll = async ({
  queryFilters,
}: {
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
}): Promise<IApiData<ILead[]>> => {
  const queryString = buildQueryString(queryFilters)
  try {
    const response = (await api.get({
      path: '/leads',
      queryString,
    })) as IApiData<ILead[]>

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findById = async (id: string): Promise<ILead> => {
  try {
    const response = await api.get({
      path: `/leads/${id}`,
    })

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const create = async ({
  lead,
  source,
}: {
  lead: ICreateLead
  source: LeadSource
}): Promise<string> => {
  try {
    const response = await api.post({
      path: getApiPathFromLeadSource(source),
      authenticate: false,
      data: lead,
    })

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

export default {
  findAll,
  findById,
  create,
}
